import React from 'react';
import { inject, observer } from 'mobx-react';
import { productsHelper } from '../../../../helpers';
import styles from './CartPaper.module.css';
import { EltyInfoButton } from './Elty/EltyInfoButton';

// Helper function to get the correct logo and dimensions based on product type
const getProductLogo = (product) => {
  if (!product) {
    return {
      src: '/products/logo_air_small.png',
      width: 45,
    };
  }

  const productName = product.name?.toLowerCase() || '';

  if (productName.includes('move-in') || productName.includes('movein')) {
    return {
      src: '/products/logo_move_in_vertical.png',
      width: 30,
    };
  } else if (productName.includes('elty')) {
    return {
      src: '/products/elty_logo_title.png',
      width: 30,
    };
  } else {
    return {
      src: '/products/logo_air_small.png',
      width: 45,
    };
  }
};

const CartPaper = ({ productsStore }) => {
  const canBuyEltyOffer = productsStore?.showEltyBundleInCart();

  // Check if Elty is already in the cart
  const hasAddedEltyOffer = productsStore?.selectedOptionalServicesWithRatePlans?.some(
    (item) => productsStore.isElty(item.product)
  );

  const handleAddEltyOffer = () => {
    productsStore && productsStore.addEltyBundleInAdditionalProducts();
  };

  const handleRemoveEltyOffer = () => {
    productsStore && productsStore.removeEltyBundleFromAdditionalProducts();
  };

  // Extract relevant data from stores
  const mainProduct = productsStore?.selectedProduct;
  const mainProductRatePlan = productsStore?.selectedProductRateplan;
  const optionalProductRatePlan = productsStore?.optionalService;
  const addonProductRatePlan = productsStore?.selectedAddOn;

  const selectedOptionalServices = productsStore?.selectedOptionalServicesWithRatePlans?.filter(
    (selectedOptionServiceWithRatePlan) =>
      // Filter out Elty products as they are displayed separately
      !productsStore.isElty(selectedOptionServiceWithRatePlan?.product)
  );

  // Helper function to get discounted price
  const getProductPriceWithDiscount = (
    productUuid,
    originalPrice,
    isEltyWithBundle = false
  ) => {
    if (!productUuid) {
      return undefined;
    }
    const parsedOriginalPrice = Number.parseFloat(originalPrice);

    // Check for Elty bundle discount first
    if (isEltyWithBundle) {
      const eltyFreeDiscount = productsStore?.discountRatePlans?.find(
        (discountRatePlan) =>
          discountRatePlan.name &&
          discountRatePlan.name.toLowerCase().includes('elty')
      );

      if (eltyFreeDiscount) {
        return productsStore.getDiscountedPrice(
          eltyFreeDiscount.id,
          parsedOriginalPrice
        );
      }
    }

    let discount = productsStore?.getDiscountFromProductUuid(productUuid);

    if (discount) {
      return productsStore.getDiscountedPrice(
        discount && discount?.discountRatePlanId,
        parsedOriginalPrice
      );
    } else {
      return undefined;
    }
  };

  // Get total price from store
  const totalPrice =
    productsStore?.calculateTotalAmountForProductsToSubscribe() || 0;

  // Get prices for individual items to display them
  const mainPrice = productsHelper?.getProductPriceFromRatePlan(
    productsStore?.selectedProductRateplan
  );

  // Check if it's Elty and eligible for free 6 months
  const isEltyWithBundle =
    mainProduct && productsStore?.isEltyWithBundleDiscount(mainProduct);

  let optionalPrice, optionalPriceDiscounted;
  if (optionalProductRatePlan) {
    optionalPrice = productsHelper?.getProductPriceFromRatePlan(
      optionalProductRatePlan
    );
    optionalPriceDiscounted = getProductPriceWithDiscount(
      productsStore?.optionalServiceProduct &&
        productsStore?.optionalServiceProduct?.uuid,
      optionalPrice
    );
  }

  const mainPriceDiscounted = getProductPriceWithDiscount(
    productsStore?.selectedProduct?.uuid,
    mainPrice,
    isEltyWithBundle
  );

  // Format price with € symbol
  const formatPrice = (amount) => `${amount.toFixed(2)} €`;

  return (
    <div className={styles.container}>
      {/* Title and Subtitle */}
      <div className={styles.header}>
        <h2 className={styles.title}>Riepilogo ordine</h2>
        <p className={styles.subtitle}>
          Rivedi i tuoi articoli prima del pagamento
        </p>
      </div>

      {/* Main Product */}
      {mainProduct && mainPrice && (
        <>
          <div className={styles.productItem}>
            <div className={styles.productContent}>
              {/* Logo with background */}
              <div className={styles.logoContainer}>
                <img
                  src={getProductLogo(mainProduct).src}
                  alt='Product Logo'
                  className={styles.logoImage}
                  style={{ width: getProductLogo(mainProduct).width }}
                />
              </div>
              <div className={styles.productInfo}>
                <span className={styles.productName}>
                  {productsStore.isAirRenewSelected
                    ? `Servizio Air`
                    : mainProduct.name}
                </span>
                <span className={styles.productDescription}>
                  {mainProductRatePlan?.name?.includes(' - ')
                    ? mainProductRatePlan?.name?.split(' - ')[1]
                    : mainProductRatePlan?.name}
                </span>
                {/* Show "primi 6 mesi gratis" for Elty bundle */}
                {isEltyWithBundle && (
                  <span className={styles.freeMonthsBadge}>
                    Primi 6 mesi gratis
                  </span>
                )}
              </div>
            </div>
            <div className={styles.priceContainer}>
              {mainPriceDiscounted !== undefined && (
                <div className={styles.originalPrice}>
                  {formatPrice(mainPrice)}
                </div>
              )}
              <span className={styles.currentPrice}>
                {formatPrice(
                  mainPriceDiscounted !== undefined
                    ? mainPriceDiscounted
                    : mainPrice
                )}
              </span>
            </div>
          </div>
          <div className={styles.divider} />
        </>
      )}

      {/* Optional Service */}
      {Object.keys(optionalProductRatePlan ?? {}).length > 0 && optionalPrice && (
        <>
          <div className={styles.productItem}>
            <div className={styles.productContent}>
              <div className={styles.logoContainer}>
                <img
                  src={
                    getProductLogo(productsStore?.optionalServiceProduct).src
                  }
                  alt='Optional Service Logo'
                  className={styles.logoImage}
                  style={{
                    width: getProductLogo(productsStore?.optionalServiceProduct)
                      .width,
                  }}
                />
              </div>
              <div className={styles.productInfo}>
                <span className={styles.productName}>
                  {optionalProductRatePlan?.name?.split(' - ')[0] ||
                    optionalProductRatePlan?.name}
                </span>
                <span className={styles.productDescription}>
                  {optionalProductRatePlan?.name?.includes(' - ')
                    ? optionalProductRatePlan?.name?.split(' - ')[1]
                    : ''}
                </span>
              </div>
            </div>
            <div className={styles.priceContainer}>
              {optionalPriceDiscounted !== undefined && (
                <div className={styles.originalPrice}>
                  {formatPrice(optionalPrice)}
                </div>
              )}
              <span className={styles.currentPrice}>
                {formatPrice(
                  optionalPriceDiscounted !== undefined
                    ? optionalPriceDiscounted
                    : optionalPrice
                )}
              </span>
            </div>
          </div>
          <div className={styles.divider} />
        </>
      )}

      {/* Add-On */}
      {Object.keys(addonProductRatePlan ?? {}).length > 0 && (
        <>
          <div className={styles.productItem}>
            <div className={styles.productContent}>
              <div className={styles.logoContainer}>
                <img
                  src={getProductLogo(addonProductRatePlan).src}
                  alt='Add-on Logo'
                  className={styles.logoImage}
                  style={{ width: getProductLogo(addonProductRatePlan).width }}
                />
              </div>
              <div className={styles.productInfo}>
                <span className={styles.productName}>
                  {addonProductRatePlan?.name?.split(' - ')[0] ||
                    addonProductRatePlan?.name}
                </span>
                <span className={styles.productDescription}>
                  {addonProductRatePlan?.name?.includes(' - ')
                    ? addonProductRatePlan?.name?.split(' - ')[1]
                    : ''}
                </span>
              </div>
            </div>
            <span className={styles.currentPrice}>
              {formatPrice(
                addonProductRatePlan?.productRatePlanCharges[0]?.pricing[0]
                  ?.price
              )}
            </span>
          </div>
          <div className={styles.divider} />
        </>
      )}

      {/* Additional Optional Services */}
      {productsStore?.isAtLeastAnAdditionalProductRatePlanSelected &&
        selectedOptionalServices?.map(
          (selectedOptionServiceWithRatePlan, index) => {
            const standardPrice = productsHelper?.getProductPriceFromRatePlan(
              selectedOptionServiceWithRatePlan?.ratePlan
            );
            const discountedPrice = getProductPriceWithDiscount(
              selectedOptionServiceWithRatePlan?.product?.uuid,
              standardPrice
            );

            return (
              <div key={index} className={styles.productItem}>
                <div className={styles.productContent}>
                  <div className={styles.logoContainer}>
                    <img
                      src={
                        getProductLogo(
                          selectedOptionServiceWithRatePlan?.product
                        ).src
                      }
                      alt='Service Logo'
                      className={styles.logoImage}
                      style={{
                        width: getProductLogo(
                          selectedOptionServiceWithRatePlan?.product
                        ).width,
                      }}
                    />
                  </div>
                  <div className={styles.productInfo}>
                    <span className={styles.productName}>
                      {selectedOptionServiceWithRatePlan?.ratePlan?.name?.split(
                        ' - '
                      )[0] || selectedOptionServiceWithRatePlan?.ratePlan?.name}
                    </span>
                    <span className={styles.productDescription}>
                      {selectedOptionServiceWithRatePlan?.ratePlan?.name?.includes(
                        ' - '
                      )
                        ? selectedOptionServiceWithRatePlan?.ratePlan?.name?.split(
                            ' - '
                          )[1]
                        : ''}
                    </span>
                  </div>
                </div>
                <div className={styles.priceContainer}>
                  {discountedPrice !== undefined && (
                    <div className={styles.originalPrice}>
                      {formatPrice(standardPrice)}
                    </div>
                  )}
                  <span className={styles.currentPrice}>
                    {formatPrice(
                      discountedPrice !== undefined
                        ? discountedPrice
                        : standardPrice
                    )}
                  </span>
                </div>
              </div>
            );
          }
        )}

      {canBuyEltyOffer && !hasAddedEltyOffer && (
        <EltyOfferCard onAdd={handleAddEltyOffer} />
      )}
      {hasAddedEltyOffer && (
        <EltyDiscountedItem onRemove={handleRemoveEltyOffer} />
      )}

      {/* Subtotal */}
      <div className={styles.summaryRow}>
        <span className={styles.summaryValue}>{formatPrice(totalPrice)}</span>
      </div>

      {/* IVA */}
      <div className={styles.summaryRow} style={{ marginBottom: 16 }}>
        <span className={styles.summaryLabel}>IVA</span>
        <span className={styles.summaryValue}>Inclusa</span>
      </div>

      {/* Total Price Separator & Line */}
      <div className={styles.totalDivider} />

      {/* Total */}
      <div className={styles.totalRow}>
        <span className={styles.totalLabel}>Totale</span>
        <span className={styles.totalValue}>{formatPrice(totalPrice)}</span>
      </div>
    </div>
  );
};

export default inject('productsStore')(observer(CartPaper));

const EltyDiscountedItem = ({ onRemove = () => {} }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const logo = getProductLogo({
    name: 'Elty',
  });

  return (
    <div
      className={styles.eltyDiscountedItem}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.productContent}>
        <span className={styles.eltyLogoContainer}>
          <img
            src={logo.src}
            alt='Product Logo'
            className={styles.logoImage}
            style={{ width: logo.width }}
          />
        </span>
        <div className={styles.productInfo}>
          <div className={styles.eltyHeader}>
            <span className={styles.productName}>Elty Salute</span>
            <EltyInfoButton />
            {isHovered && (
              <button className={styles.removeButton} onClick={onRemove}>
                ×
              </button>
            )}
          </div>
          <div className={styles.eltyBadgeContainer}>
            <span className={styles.freeBadge}>Offerta gratuita</span>
          </div>
        </div>
      </div>
      <div className={styles.priceContainer}>
        <div className={styles.discountedPrice}>0.00 €</div>
        <span className={styles.originalPrice}>9.99 €/mese</span>
      </div>
    </div>
  );
};

const EltyOfferCard = ({ onAdd = () => {} }) => {
  const logo = getProductLogo({
    name: 'Elty',
  });

  return (
    <div className={styles.eltyOfferCard}>
      <div className={styles.offerHeader}>
        <div className={styles.offerLogoContainer}>
          <img
            src={logo.src}
            alt='Elty Logo'
            className={styles.logoImage}
            style={{ width: logo.width }}
          />
        </div>
        <div className={styles.offerContent}>
          <div className={styles.offerTitleRow}>
            <span className={styles.offerTitle}>Elty Salute</span>
            <span className={styles.freeMonthsBadgeGreen}>GRATIS 6 MESI</span>
            <EltyInfoButton />
          </div>
          <p className={styles.offerDescription}>
            Accesso immediato a medici di famiglia e pediatri certificati,
            attivi 24/7
          </p>
          <div className={styles.pricingInfo}>
            <span className={styles.freeForMonths}>Gratuito per 6 mesi</span>
            <span className={styles.priceAfter}>9.99 €/mese dopo</span>
          </div>
        </div>
      </div>

      <button className={styles.addButton} onClick={onAdd}>
        <span className={styles.addButtonIcon}>+</span>
        Aggiungi offerta gratuita
      </button>
    </div>
  );
};
